import { useMemo } from "react";
import { formatStatCount, deriveProgressColor } from "../utils/playerStatsUtils";

/**
 * Hook to extract and compute stats data from season stats blocks
 * Shared between Player and Goalkeeper stats components
 */
export const useStatsData = (seasonStats, options = {}) => {
  const { filterGoalkeeper = false } = options;

  // Extract blocks from season stats
  const blocks = useMemo(() => {
    const rawBlocks = seasonStats?.blocks || [];
    if (!filterGoalkeeper) return rawBlocks;

    // Filter out goalkeeper block for outfield players
    return Array.isArray(rawBlocks)
      ? rawBlocks.filter(
          (b) =>
            !(typeof b?.name === "string" && b.name.toLowerCase() === "goalkeeper")
        )
      : [];
  }, [seasonStats, filterGoalkeeper]);

  // Team info
  const team = seasonStats?.team;
  const teamName = team?.name || "Team";
  const teamLogo = team?.logo || null;
  const seasonName = seasonStats?.season_name || seasonStats?.season_id || "Season";

  // Helper function to get stat values from blocks
  const getStatValue = (blockName, statKey) => {
    const block = blocks.find((b) => b.name === blockName);
    const stat = block?.stats?.find((s) => s.key === statKey);
    return stat?.value || 0;
  };

  // Create a flattened stats object for performance cards
  const stats = useMemo(() => {
    return blocks.reduce((acc, block) => {
      if (!Array.isArray(block?.stats)) return acc;
      block.stats.forEach((stat) => {
        const value = stat?.value;
        if (value !== null && typeof value === "object") {
          // First, check if stat key matches a property in the value object
          if (stat.key && typeof value[stat.key] === "number") {
            acc[stat.key] = value[stat.key];
          } else if (typeof value.total === "number") {
            acc[stat.key] = value.total;
          } else if (typeof value.average === "number") {
            acc[stat.key] = value.average;
          } else if (typeof value.goals === "number") {
            acc[stat.key] = value.goals;
          } else if (typeof value.scored === "number") {
            acc[stat.key] = value.scored;
          }
        } else {
          acc[stat.key] = value;
        }
      });
      return acc;
    }, {});
  }, [blocks]);

  // Calculate starts and appearances for validation
  const startsValue = getStatValue("Overview", "first") || 0;
  const appearancesValue =
    getStatValue("Overview", "matches") || getStatValue("Overview", "appearances") || 0;
  const minutesValue = getStatValue("Overview", "minutes_played") || 0;

  // Validate starts/appearances: only hide if starts > 0 but appearances = 0 (invalid scenario)
  const shouldShowStartsAppearances = !(startsValue > 0 && appearancesValue === 0);

  // Filter performance cards: hide appearances if minutes > 0 but appearances = 0
  const shouldShowAppearancesCard = !(minutesValue > 0 && appearancesValue === 0);

  // Check if stats are empty
  const hasStats =
    seasonStats && Array.isArray(blocks) && blocks.length > 0;

  return {
    blocks,
    team,
    teamName,
    teamLogo,
    seasonName,
    stats,
    getStatValue,
    startsValue,
    appearancesValue,
    minutesValue,
    shouldShowStartsAppearances,
    shouldShowAppearancesCard,
    hasStats,
  };
};

/**
 * Hook to build section items for stats sections
 * Handles progress bars for accuracy stats
 */
export const useBuildSectionItems = () => {
  const buildSectionItems = (block) => {
    if (!block?.stats) {
      return [];
    }

    // Helpers
    const findStatByLabel = (labels) => {
      const normalized = Array.isArray(labels) ? labels : [labels];
      return block.stats.find(
        (stat) =>
          typeof stat.label === "string" &&
          normalized.some(
            (candidate) => stat.label.toLowerCase() === candidate.toLowerCase()
          )
      );
    };

    const isTargetLabel = (stat, label) =>
      typeof stat.label === "string" &&
      stat.label.toLowerCase() === label.toLowerCase();

    const findTotalForStat = (candidates) => {
      const stat = findStatByLabel(candidates);
      if (!stat) return null;
      const numeric = Number(stat.value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const isPassBlock =
      typeof block.name === "string" && block.name.toLowerCase().includes("pass");
    const isDribbleBlock =
      typeof block.name === "string" && block.name.toLowerCase().includes("drib");
    const isGoalkeeperBlock =
      typeof block.name === "string" && block.name.toLowerCase().includes("goalkeeper");

    const totalPassesStat = isPassBlock ? findStatByLabel("passes") : null;
    const totalPassesValueRaw = Number(totalPassesStat?.value);
    const totalPassesValue = Number.isFinite(totalPassesValueRaw)
      ? totalPassesValueRaw
      : null;

    const applyProgress = ({ total, made, ariaBase }) => {
      const safeMade = Number.isFinite(made) ? made : null;
      const safeTotal = Number.isFinite(total) ? total : null;

      const ratio =
        safeMade !== null && safeTotal !== null && safeTotal > 0
          ? Math.min(Math.max(safeMade / safeTotal, 0), 1)
          : 0;

      const rawPercentage = ratio * 100;
      const roundedPercentage = Number.isFinite(rawPercentage)
        ? Number(rawPercentage.toFixed(1))
        : 0;
      const percentLabel = `${roundedPercentage.toFixed(1)}%`;

      return {
        displayAsProgress: true,
        progress: ratio,
        progressPercent: roundedPercentage,
        progressLabel: percentLabel,
        progressMade: safeMade,
        progressTotal: safeTotal,
        progressAriaLabel:
          safeTotal !== null
            ? `${safeMade ?? 0} ${ariaBase} out of ${safeTotal} (${percentLabel})`
            : `${safeMade ?? 0} ${ariaBase} (${percentLabel})`,
        progressColor: deriveProgressColor(roundedPercentage),
      };
    };

    return block.stats.flatMap((stat) => {
      const value = stat?.value;
      const formattedValue = formatStatCount({ [stat.key]: value }, [stat.key]);

      // If value is an object, expand into multiple rows
      if (value !== null && typeof value === "object") {
        return Object.entries(value).map(([subKey, subVal]) => {
          const displayVal = formatStatCount({ [subKey]: subVal }, [subKey]);
          const labelMatches = stat.label?.toLowerCase() === subKey?.toLowerCase();
          const label = labelMatches ? stat.label : `${stat.label} ${subKey}`;
          return {
            label,
            value: displayVal,
            key: `${stat.key}_${subKey}`,
            rawValue: subVal,
          };
        });
      }

      const baseItem = {
        label: stat.label,
        value: formattedValue,
        key: stat.key,
        rawValue: value,
      };

      // Apply progress bars for specific stats
      if (isPassBlock) {
        if (isTargetLabel(stat, "accurate passes")) {
          const accurateValue = Number(value);
          return {
            ...baseItem,
            ...applyProgress({
              total: totalPassesValue,
              made: Number.isFinite(accurateValue) ? accurateValue : null,
              ariaBase: "accurate passes",
            }),
            progressGradient: "linear-gradient(to right, #FFB607, #FFEDC2)",
          };
        }

        if (isTargetLabel(stat, "crosses (accurate)")) {
          const accurateCrosses = Number(value);
          const totalCrosses = findTotalForStat(["crosses", "crosses total"]);
          return {
            ...baseItem,
            ...applyProgress({
              total: Number.isFinite(totalCrosses) ? totalCrosses : null,
              made: Number.isFinite(accurateCrosses) ? accurateCrosses : null,
              ariaBase: "accurate crosses",
            }),
          };
        }

        if (isTargetLabel(stat, "long balls (accurate)")) {
          const accurateLongBalls = Number(value);
          const totalLongBalls = findTotalForStat(["long balls", "long balls total"]);
          return {
            ...baseItem,
            ...applyProgress({
              total: Number.isFinite(totalLongBalls) ? totalLongBalls : null,
              made: Number.isFinite(accurateLongBalls) ? accurateLongBalls : null,
              ariaBase: "accurate long balls",
            }),
            progressGradient: "linear-gradient(to right, #FF5066, #FF9FAB)",
          };
        }
      }

      // No progress bar for dribbles (successful) - just show the value

      if (isGoalkeeperBlock && isTargetLabel(stat, "runs out (successful)")) {
        const successfulRuns = Number(value);
        const totalRuns = findTotalForStat(["runs out"]);
        return {
          ...baseItem,
          ...applyProgress({
            total: Number.isFinite(totalRuns) ? totalRuns : null,
            made: Number.isFinite(successfulRuns) ? successfulRuns : null,
            ariaBase: "successful runs out",
          }),
          progressGradient: "linear-gradient(to right, #43BD17, #94F96F)",
        };
      }

      return baseItem;
    });
  };

  return { buildSectionItems };
};

/**
 * Hook to filter performance cards based on validation rules
 */
export const useFilteredPerformanceCards = (cards, shouldShowAppearancesCard) => {
  return useMemo(() => {
    return cards.filter((card) => {
      if (card.key === "appearances") {
        return shouldShowAppearancesCard;
      }
      return true;
    });
  }, [cards, shouldShowAppearancesCard]);
};
